import { createContext, useContext, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import FooterCta from "@/components/landing/FooterCta";
import { useAuth } from "@/lib/auth";
import { PROFILE_LINKS, isProfileLinkActive } from "@/lib/accountNav";

const InsideShell = createContext(false);

function ProfileSidebar({ pathname }) {
  return (
    <aside className="mb-6 flex gap-1 overflow-x-auto lg:mb-0 lg:flex-col" data-testid="profile-sidebar">
      {PROFILE_LINKS.map((l) => (
        <Link
          key={l.to}
          to={l.to}
          data-testid={`profile-link-${l.to.split("/").pop()}`}
          className={`whitespace-nowrap rounded-lg px-3 py-2 text-sm transition ${
            isProfileLinkActive(pathname, l) ? "bg-surface-2 text-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {l.label}
        </Link>
      ))}
    </aside>
  );
}

function SessionSkeleton() {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-4 py-28 text-center">
      <div className="skeleton mb-4 h-12 w-12 rounded-full" />
      <div className="skeleton h-6 w-48" />
      <div className="skeleton mt-3 h-4 w-64" />
    </div>
  );
}

/** Site chrome (header, footer) shared by every non-admin page. */
export function SiteFrame({ children }) {
  const { pathname } = useLocation();
  return (
    <div id="app" className="section-band flex min-h-screen w-full min-w-0 max-w-full flex-col overflow-x-clip">
      <Header />
      <main className="section-band w-full min-w-0 max-w-full flex-1 overflow-x-clip">{children}</main>
      <div className="section-band relative z-10 w-full min-w-0 max-w-full overflow-x-clip">
        {(pathname === "/" || pathname === "/contact") && <FooterCta />}
        <Footer />
      </div>
    </div>
  );
}

/**
 * Layout for app pages. `bare` pages render their own PaisaCX page wrapper;
 * everything else gets the standard max-w-7xl container. Pages under
 * /profile get the account sidebar; a nested AppShell renders its children only.
 */
export default function AppShell({ children, requireAuth = true, bare = false }) {
  const nested = useContext(InsideShell);
  const { user } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const inProfile = pathname === "/profile" || pathname.startsWith("/profile/");

  useEffect(() => {
    if (nested || !requireAuth) return;
    if (user === false) nav("/login", { replace: true, state: { from: pathname } });
  }, [nested, user, requireAuth, nav, pathname]);

  if (nested) return children;

  let body;
  if (requireAuth && !user) {
    body = <SessionSkeleton />;
  } else if (inProfile) {
    body = (
      <div className="page-fade mx-auto max-w-7xl gap-8 px-4 py-10 lg:grid lg:grid-cols-[220px_1fr]">
        <ProfileSidebar pathname={pathname} />
        <div className="min-w-0">{children}</div>
      </div>
    );
  } else if (bare) {
    body = children;
  } else {
    body = <div className="page-fade mx-auto w-full max-w-7xl min-w-0 px-4 py-8 sm:py-10">{children}</div>;
  }

  return (
    <InsideShell.Provider value>
      <SiteFrame>{body}</SiteFrame>
    </InsideShell.Provider>
  );
}
